import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {AuthService, BaseService, RecursiveHelper} from '@hassel-it/angular-common';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Package} from './package';
import {catchError, concatMap, from, mergeMap, Observable, of, toArray} from "rxjs";
import {map} from "rxjs/operators";
import {ShipmentEvent} from "./shipment-event";
import * as moment from "moment/moment";
import {ShipmentProductLine} from "./shipment-product-line";
import {ShipmentsService} from "./shipments.service";

@Injectable()
export class ShipmentPackagesService extends BaseService {

    constructor(public http: HttpClient, public authService: AuthService, public shipmentsService: ShipmentsService) {
        super(http, authService);
    }

    createPackageFromShipmentWithProducts(shipment,
                                          productLineQueue: ShipmentProductLine[],
                                          activeShipmentPackagings,
                                          numberLables = 1,
                                          branch,
                                          debugMode,
                                          sumWeightOverride,
                                          enableDg,
                                          addToQueue): Observable<any> {

        if (shipment.carrier_code === '999' && shipment.branch === 'hoe') {
            const url = 'https://boxen.10-4.dk';
            const newTab = window.open();
            if (newTab) {
                newTab.opener = null;
                newTab.location = url;
            } else {
                alert('Failed to open a new tab. Please check your browser settings.');
            }
            return of(true);
        }

        const products = [];

        for (const shipmentProductLine of productLineQueue) {
            products.push({
                amount_scanned: shipmentProductLine.amount_scanned,
                shipment_product_line_id: shipmentProductLine.id
            });
        }

        const shipmentPackage = Object.create(Package.prototype);

        shipmentPackage.package_info = {
            products: products,
            sumWeightOverride: sumWeightOverride,
            enableDg: enableDg
        }

        const activeType = activeShipmentPackagings.find((item) => {
            return item.selected === true
        });
        if (activeType) {
            shipmentPackage.package_type = activeType.packageType;
        }

        shipmentPackage.number_kolli = numberLables;

        // tslint:disable-next-line:max-line-length
        return this.addShipmentPackage(shipment.id, shipmentPackage, branch, debugMode, 'object', addToQueue).pipe(concatMap((returnData) => {
                    return from(productLineQueue).pipe(
                        concatMap((item) => {
                            const status = (item.amount_picked + item.amount_scanned === item.amount) ? 'ready_to_ship' : 'packing_started';
                            const newEvent = ShipmentEvent.fromJSON({
                                meta_data: {amount_scanned: item.amount_scanned},
                                foreign_key: item.id,
                                model: 'ShipmentProductLines',
                                status_text: null,
                                date: moment(),
                                shipment_status: status,
                            });
                            return this.shipmentsService.createShipmentEvent(newEvent); // Sequential call for each item
                        }),
                        toArray(),
                        map(() => returnData)
                    );
                }
            ),
        )
    }

    /**
     * Get the quote price for a given shipment package.
     * @param shipmentId
     * @param shipmentPackage
     * @param branch
     */
    getPrice(shipmentId: string, shipmentPackage: Package, branch: string) {
        this.reloadHeaders();
        let params = new HttpParams();
        params = params.set('branch', branch);
        return this.http.post(environment.apiBase + '/api/ten-four/shipments/' + shipmentId + '/shipment_packages/fetch-price', shipmentPackage, {
            headers: this.httpHeaders,
            params: params,
            responseType: 'json'
        }).pipe(map((response: any) => {
            shipmentPackage.price = response.data?.carrier_price?.price;
            if (shipmentPackage.price) {
                shipmentPackage.price = (shipmentPackage.price * 1.06) * 1.25; // 6% markup, then 25% VAT
            }
            return shipmentPackage;
        }));
    }

    // tslint:disable-next-line:max-line-length
    addShipmentPackage(shipmentId: string, shipmentPackage: Package, branch: string, debug: boolean, format: 'object' | 'pdf', addToQueue ): Observable<Package> {
        this.reloadHeaders();

        let params = new HttpParams();
        params = params.set('branch', branch);

        if (debug) {
            params = params.set('debug', 'true');
        }
        if (addToQueue) {
            params = params.set('addToQueue', 'true');
        }
        if (format === 'pdf') {
            params = params.set('pdf', 'true');
        }

        let options;
        options = {
                headers: this.httpHeaders,
                params: params,
                responseType: 'json'
            }

        // tslint:disable-next-line:max-line-length
        return this.http.post(environment.apiBase + '/api/ten-four/shipments/' + shipmentId + '/shipment_packages', shipmentPackage, options).pipe(mergeMap((response: any) => {
            // if (format === 'pdf') {
            //     return of(response);
            // }
            return of(response.data as Package);
        }));
    }

    printAdrForShipmentPackage(shipmentId: string, shipmentPackageId: Package, branch: string): Observable<Blob | MediaSource> {
        this.reloadHeaders();

        let params = new HttpParams();
        params = params.set('branch', branch);

        return this.http.get(environment.apiBase + '/api/ten-four/shipments/' + shipmentId + '/shipment_packages/' + shipmentPackageId + '/print-adr', {
            headers: this.httpHeaders,
            params: params,
            responseType: 'blob'
        }).pipe(map((response: any) => {
            return new Blob([response], {type: 'application/pdf'});
        }));
    }

    printPdfForShipmentPackage(shipmentId: string, shipmentPackageId: Package, branch: string): Observable<Blob | MediaSource> {
        this.reloadHeaders();

        let params = new HttpParams();
        params = params.set('branch', branch);

        return this.http.get(environment.apiBase + '/api/ten-four/shipments/' + shipmentId + '/shipment_packages/' + shipmentPackageId + '/pdf', {
            headers: this.httpHeaders,
            params: params,
            responseType: 'blob'
        }).pipe(map((response: any) => {
            return new Blob([response], {type: 'application/pdf'});
        }));
    }

    deleteShipmentPackage(shipmentPackage: Package): Observable<boolean> {

        this.reloadHeaders();
        const url = environment.apiBase + '/api/ten-four/shipments/' + shipmentPackage.shipment_id + '/shipment_packages/' + shipmentPackage.id;

        return this.http.delete(url, { headers: this.httpHeaders})
            .pipe(
                map(response => true),
                catchError((error) => {
                    console.error('Error deleting shipment package:', error);
                    return of(false);
                })
            );

    }

    fetchShipmentPackagePrice(shipmentPackage: Package): Observable<any> {

        this.reloadHeaders();
        const url = environment.apiBase + '/api/ten-four/shipments/' + shipmentPackage.shipment_id + '/shipment_packages/' + shipmentPackage.id + '/price';

        return this.http.get(url, { headers: this.httpHeaders})
            .pipe(
                map((response:any) => {
                    return response.data;
                }),
                catchError((error) => {
                    console.error('Error deleting shipment package:', error);
                    return of(false);
                })
            );

    }

    printAllLabels(shipmentId:string, data) {
        this.reloadHeaders();
        const url = environment.apiBase + '/api/ten-four/shipments/' + shipmentId + '/shipment_packages/print-many';

        return this.http.post(url, data,{ headers: this.httpHeaders})
            .pipe(
                map((response:any) => {
                    return response.data;
                }),
                catchError((error) => {
                    console.error('Error shipment packages:', error);
                    return of(false);
                })
            );
    }
}
